import dayjs from 'dayjs';
import { useMemo } from 'react';

import useMediaQuery, { Breakpoint } from '@/hooks/useMediaQuery';
import { pricesHelper } from '@/utils/pricesHelper';

import { StatusAlertProps } from './StatusAlert';
import {
  AlertId,
  getAlertConfig,
  getDayParityAvatar,
  StatusAlertActionId,
} from './statusAlerts.config';

export type StatusAlertActionHandlers = Partial<
Record<StatusAlertActionId, () => void>
>;

export interface UseResolvedStatusAlertOptions {
  id: AlertId | null;
  handlers: StatusAlertActionHandlers;
  onClose?: () => void;
  amountalert?: number | null; // Importe de la operación del aviso por evento (Pepper One).
}

export default function useResolvedStatusAlert({
  id,
  handlers,
  onClose,
  amountalert,
}: UseResolvedStatusAlertOptions): StatusAlertProps {
  const isDesktop = useMediaQuery(Breakpoint.Tablet);
  return useMemo(() => {
    if (!id) return {} as StatusAlertProps;

    const config = getAlertConfig(id);
    const AppDesktopConfig = isDesktop ? config.web : config.app;
    const formattedAmount = amountalert != null ? pricesHelper(amountalert, true) ?? '' : '';
    const fillAmount = (text: string) => text.replace('{amount}', formattedAmount);
    // Los avisos por evento muestran avatar (según la paridad del día) solo en
    // desktop; en tablet/móvil no se pinta icono, igual que los métricos. El
    // resto usa la imagen fija de la copy.
    const image = config.avatars && isDesktop
      ? getDayParityAvatar(config.avatars, dayjs().date())
      : AppDesktopConfig.image;

    return {
      image,
      title: fillAmount(AppDesktopConfig.title),
      message: fillAmount(AppDesktopConfig.message),
      actionLabel: config.ctaLabel,
      onAction: handlers[config.actionId],
      variant: config.variant,
      onClose,
    };
  }, [id, handlers, onClose, isDesktop, amountalert]);
}
